// AuthContext.js
import React, { createContext, useReducer, useEffect } from 'react';
import { auth, authReady } from './firebaseConfig';
import { getRedirectResult, onAuthStateChanged, signInWithEmailAndPassword, signOut, GoogleAuthProvider, signInWithPopup, signInWithRedirect } from 'firebase/auth';
import { requiresEmailVerification } from './authVerification';
import {
    loadUserProfile,
    recordEarlyAccessAcknowledgement,
    upsertUserProfile,
} from './userProfile';

const AuthContext = createContext();

const initialState = {
    user: null,
    profile: null,
    profileLoading: false,
    loading: true,
    error: '',
};

const authReducer = (state, action) => {
    //console.log('in auth reducer' + JSON.stringify(action) ) ;
    switch (action.type) {
        case 'LOGIN':
            return { ...state, user: action.payload, loading: false };
        case 'LOGOUT':
            return { user: null, profile: null, profileLoading: false, loading: false, error: '' };
        case 'SET_LOADING':
            return { ...state, loading: true };
        case 'SET_PROFILE_LOADING':
            return { ...state, profileLoading: action.payload };
        case 'SET_PROFILE':
            return { ...state, profile: action.payload, profileLoading: false };
        default:
            return state;
    }
};

export const AuthProvider = ({ children }) => {
    const [authState, dispatch] = useReducer(authReducer, initialState);

    useEffect(() => {
        let unsubscribe = () => {};
        let active = true;
        void authReady.then(() => getRedirectResult(auth)).catch((error) => {
            console.error('Firebase redirect sign-in failed', error);
        }).finally(() => {
          if (!active) return;
          unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                dispatch({ type: 'LOGIN', payload: user });
            } else {
                dispatch({ type: 'LOGOUT', payload: user });
            }
          });
        });

        return () => { active = false; unsubscribe(); };
    }, []);

    useEffect(() => {
        const uid = authState.user?.uid;
        let active = true;

        if (!uid || requiresEmailVerification(authState.user)) return undefined;

        dispatch({ type: 'SET_PROFILE_LOADING', payload: true });
        loadUserProfile(uid)
            .then((profile) => {
                if (active) {
                    dispatch({ type: 'SET_PROFILE', payload: profile });
                }
            })
            .catch((err) => {
                console.error(err);
                if (active) {
                    dispatch({ type: 'SET_PROFILE', payload: null });
                }
            });

        return () => {
            active = false;
        };
    }, [authState.user]);



    const loginWithEmail = async (email, password) => {
        dispatch({ type: 'SET_LOADING' });
        try {
            const creds = await signInWithEmailAndPassword(auth, email.trim(), password);
            dispatch({ type: 'LOGIN', payload: creds.user });

        }
        catch (err) {
            dispatch({ type: 'LOGOUT' });

            console.error(err);
            alert(err.message);
        }
    };

    const loginWithGoogle = async () => {
        const provider = new GoogleAuthProvider();
        dispatch({ type: 'SET_LOADING' });
        await authReady;
        let result;
        try {
          result = await signInWithPopup(auth, provider);
        } catch (error) {
          if (['auth/popup-blocked', 'auth/cancelled-popup-request', 'auth/operation-not-supported-in-this-environment'].includes(error?.code)) {
            await signInWithRedirect(auth, provider);
            return;
          }
          throw error;
        }
        await upsertUserProfile(result.user, {
            authProvider: 'google',
            name: result.user.displayName || null,
        });
    };

    const logout = async () => {
        dispatch({ type: 'SET_LOADING' });
        await signOut(auth);
    };

    const acknowledgeEarlyAccess = async () => {
        const uid = authState.user?.uid;
        if (!uid) return;

        await recordEarlyAccessAcknowledgement(uid);
        const profile = await loadUserProfile(uid);
        dispatch({ type: 'SET_PROFILE', payload: profile });
    };

    return (
        <AuthContext.Provider value={{ authState, loginWithEmail, loginWithGoogle, logout, acknowledgeEarlyAccess }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return React.useContext(AuthContext);
};
